import type { AvailableUnaddressedMailingProductsDto } from '@/services/ws-post/src/main/resources/rest-api';
import { UnaddressedMailingProduct, UnaddressedMailingType } from '@/services/ws-post/src/main/resources/rest-api';

import { filterHolidayDays, isWeekday } from './helpers.ts';

const DEFAULT_MAILING_PRODUCTS_WITHOUT_FLAP: UnaddressedMailingProduct[] = Object.values(UnaddressedMailingProduct).filter(
  (product) => product !== UnaddressedMailingProduct.FLAP,
);

export const isFlapProduct = (product: UnaddressedMailingProduct | undefined): boolean => product === UnaddressedMailingProduct.FLAP;

export const shouldDisableOfficialUnaddressedMailingType = (product: UnaddressedMailingProduct | undefined): boolean =>
  isFlapProduct(product);

export const getProductsForUnaddressedMailingType = (
  available: AvailableUnaddressedMailingProductsDto | undefined,
  mailingType: UnaddressedMailingType | undefined,
): UnaddressedMailingProduct[] => {
  if (!available) {
    return DEFAULT_MAILING_PRODUCTS_WITHOUT_FLAP;
  }

  if (mailingType === UnaddressedMailingType.OFFICIAL) {
    return available.officialProducts;
  }

  return available.commercialProducts;
};

const REVISED_DELIVERY_DAYS_FROM_YEAR = 2027;

const isFlapDistributionDateAllowed = (date: Date, isRevisedPromopostDeliveryDaysEnabled = false): boolean => {
  const day = date.getDay();
  const isRevisedDeliveryDate = isRevisedPromopostDeliveryDaysEnabled && date.getFullYear() >= REVISED_DELIVERY_DAYS_FROM_YEAR;

  if (isRevisedDeliveryDate) {
    return day === 3;
  }

  return day === 2 || day === 3;
};

const isRevisedNonFlapDistributionDateAllowed = (date: Date): boolean => {
  const day = date.getDay();

  return day === 3 || day === 5;
};

export const filterDistributionDateForProduct = (
  product: UnaddressedMailingProduct | undefined,
  date: Date,
  isRevisedPromopostDeliveryDaysEnabled = false,
): boolean => {
  if (!filterHolidayDays(date)) {
    return false;
  }

  if (isFlapProduct(product)) {
    return isFlapDistributionDateAllowed(date, isRevisedPromopostDeliveryDaysEnabled);
  }

  const isRevisedDeliveryDate = isRevisedPromopostDeliveryDaysEnabled && date.getFullYear() >= REVISED_DELIVERY_DAYS_FROM_YEAR;

  if (isRevisedDeliveryDate) {
    return isRevisedNonFlapDistributionDateAllowed(date);
  }

  return isWeekday(date);
};

export const isDistributionDateValidForProduct = (
  product: UnaddressedMailingProduct | undefined,
  date: Date,
  isRevisedPromopostDeliveryDaysEnabled = false,
): boolean => filterDistributionDateForProduct(product, date, isRevisedPromopostDeliveryDaysEnabled);
